import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import AnalyticsScripts from "@/components/AnalyticsScripts";
import { useWebVitals } from "@/hooks/useWebVitals";
import ScrollToTop from "@/components/ScrollToTop";
import AnimatedOutlet from "@/components/AnimatedOutlet";

// Eagerly load the main page for fast initial load
import Index from "./pages/Index";

// Lazy load other pages to reduce initial bundle size
const PropertyDetails = lazy(() => import("./pages/PropertyDetails"));
const AllProperties = lazy(() => import("./pages/AllProperties"));
const AllServices = lazy(() => import("./pages/AllServices"));
const ServiceDetails = lazy(() => import("./pages/ServiceDetails"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Accessibility = lazy(() => import("./pages/Accessibility"));
const Auth = lazy(() => import("./pages/Auth"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Insights = lazy(() => import("./pages/Insights"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const GoldCoast = lazy(() => import("./pages/neighborhoods/GoldCoast"));
const LincolnPark = lazy(() => import("./pages/neighborhoods/LincolnPark"));
const RiverNorth = lazy(() => import("./pages/neighborhoods/RiverNorth"));
const Lakeview = lazy(() => import("./pages/neighborhoods/Lakeview"));
const WickerPark = lazy(() => import("./pages/neighborhoods/WickerPark"));
const About = lazy(() => import("./pages/About"));

const queryClient = new QueryClient();

function WebVitalsReporter() {
  useWebVitals();
  return null;
}

// Minimal loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem={false}>
        <AuthProvider>
          <TooltipProvider>
            <AnalyticsScripts />
            <WebVitalsReporter />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route element={<AnimatedOutlet />}>
                    <Route path="/" element={<Index />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/properties" element={<AllProperties />} />
                    <Route path="/property/:slug" element={<PropertyDetails />} />
                    <Route path="/services" element={<AllServices />} />
                    <Route path="/services/:slug" element={<ServiceDetails />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/accessibility" element={<Accessibility />} />
                    <Route path="/neighborhoods/gold-coast" element={<GoldCoast />} />
                    <Route path="/neighborhoods/lincoln-park" element={<LincolnPark />} />
                    <Route path="/neighborhoods/river-north" element={<RiverNorth />} />
                    <Route path="/neighborhoods/lakeview" element={<Lakeview />} />
                    <Route path="/neighborhoods/wicker-park" element={<WickerPark />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/insights" element={<Insights />} />
                    <Route path="/insights/:slug" element={<BlogPost />} />
                    <Route path="/contact" element={<Contact />} />
                    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
